export type ChartType = 'bar' | 'line' | 'pie' | 'table'
export type AggregateFn = 'count' | 'sum' | 'avg'

export interface ReportFilter {
  field: string
  operator: 'equals' | 'contains'
  value: string
}

export interface ReportConfig {
  datasetId: string
  /** Optional curated join (see data/joins.ts). Left join semantics. */
  joinId?: string | null
  columns: string[]
  filters: ReportFilter[]
  groupBy: string | null
  metricField: string | null
  metricFn: AggregateFn
  chartType: ChartType
}

export interface ReportDef {
  id: string
  name: string
  description: string
  category: string
  config: ReportConfig
  lastRun: string
}

export const reportCategories = [
  'Appointments',
  'Results',
  'Clearances',
  'Experience',
] as const

export const premadeReports: ReportDef[] = [
  {
    id: 'master-appointments',
    name: 'Master Appointments Report',
    description:
      'Full listing of appointments, filterable by status, company, and site.',
    category: 'Appointments',
    lastRun: '2026-07-09',
    config: {
      datasetId: 'appointments',
      columns: [
        'id',
        'participant',
        'company',
        'testType',
        'collectionSite',
        'status',
        'apptDate',
      ],
      filters: [],
      groupBy: null,
      metricField: null,
      metricFn: 'count',
      chartType: 'table',
    },
  },
  {
    id: 'appts-by-booking-method',
    name: 'Appointments by Booking Method',
    description: 'Appointment volume broken down by how they were booked.',
    category: 'Appointments',
    lastRun: '2026-07-08',
    config: {
      datasetId: 'appointments',
      columns: ['bookingMethod'],
      filters: [],
      groupBy: 'bookingMethod',
      metricField: null,
      metricFn: 'count',
      chartType: 'bar',
    },
  },
  {
    id: 'appts-by-agent',
    name: 'Appointments by Booking Agent',
    description: 'Total appointments booked per agent for a chosen date range.',
    category: 'Appointments',
    lastRun: '2026-07-08',
    config: {
      datasetId: 'appointments',
      columns: ['bookingAgent'],
      filters: [],
      groupBy: 'bookingAgent',
      metricField: null,
      metricFn: 'count',
      chartType: 'bar',
    },
  },
  {
    id: 'appt-status-breakdown',
    name: 'Appointment Status Breakdown',
    description:
      'Completed vs. rescheduled, cancelled, and no-show appointments.',
    category: 'Appointments',
    lastRun: '2026-07-07',
    config: {
      datasetId: 'appointments',
      columns: ['status'],
      filters: [],
      groupBy: 'status',
      metricField: null,
      metricFn: 'count',
      chartType: 'pie',
    },
  },
  {
    id: 'master-results',
    name: 'Master Results Report',
    description: 'All lab-confirmed test results with turnaround detail.',
    category: 'Results',
    lastRun: '2026-07-09',
    config: {
      datasetId: 'results',
      columns: [
        'id',
        'participant',
        'company',
        'testCategory',
        'result',
        'lab',
        'collectedDate',
        'turnaroundHrs',
      ],
      filters: [],
      groupBy: null,
      metricField: null,
      metricFn: 'count',
      chartType: 'table',
    },
  },
  {
    id: 'results-by-outcome',
    name: 'Results by Outcome',
    description:
      'Distribution of negative, non-negative, positive, and cancelled results.',
    category: 'Results',
    lastRun: '2026-07-08',
    config: {
      datasetId: 'results',
      columns: ['result'],
      filters: [],
      groupBy: 'result',
      metricField: null,
      metricFn: 'count',
      chartType: 'pie',
    },
  },
  {
    id: 'lab-turnaround',
    name: 'Average Lab Turnaround',
    description: 'Average result turnaround hours by laboratory.',
    category: 'Results',
    lastRun: '2026-07-06',
    config: {
      datasetId: 'results',
      columns: ['lab', 'turnaroundHrs'],
      filters: [],
      groupBy: 'lab',
      metricField: 'turnaroundHrs',
      metricFn: 'avg',
      chartType: 'bar',
    },
  },
  {
    id: 'clearance-status',
    name: 'Preemptive Clearances by Status',
    description: 'Clearance requests grouped by current status.',
    category: 'Clearances',
    lastRun: '2026-07-08',
    config: {
      datasetId: 'clearances',
      columns: ['status'],
      filters: [],
      groupBy: 'status',
      metricField: null,
      metricFn: 'count',
      chartType: 'bar',
    },
  },
  {
    id: 'clearance-master',
    name: 'Clearance Requests Listing',
    description:
      'Detailed listing of all clearance requests and processing times.',
    category: 'Clearances',
    lastRun: '2026-07-07',
    config: {
      datasetId: 'clearances',
      columns: [
        'id',
        'participant',
        'company',
        'clearanceType',
        'status',
        'requestedDate',
        'processingDays',
      ],
      filters: [],
      groupBy: null,
      metricField: null,
      metricFn: 'count',
      chartType: 'table',
    },
  },
  {
    id: 'survey-ratings-by-site',
    name: 'Survey Ratings by Collection Site',
    description: 'Average participant satisfaction rating per collection site.',
    category: 'Experience',
    lastRun: '2026-07-09',
    config: {
      datasetId: 'surveys',
      columns: ['collectionSite', 'rating'],
      filters: [],
      groupBy: 'collectionSite',
      metricField: 'rating',
      metricFn: 'avg',
      chartType: 'bar',
    },
  },
  {
    id: 'survey-comments',
    name: 'Participant Survey Comments',
    description: 'Completed participant surveys with ratings and comments.',
    category: 'Experience',
    lastRun: '2026-07-05',
    config: {
      datasetId: 'surveys',
      columns: [
        'participant',
        'company',
        'collectionSite',
        'rating',
        'comment',
        'surveyDate',
      ],
      filters: [],
      groupBy: null,
      metricField: null,
      metricFn: 'count',
      chartType: 'table',
    },
  },
]

export function getReport(id: string): ReportDef | undefined {
  return premadeReports.find((r) => r.id === id)
}
